import lexer, { type Token } from 'js-tokens';
import type { AnalyzerWorker } from '~/lib/static-analysis/analyzer';
import type MagicString from '~/lib/static-analysis/magic';

interface CurrentToken {
  delimiter: string;
  token: Token;
  offset: number;
}

const keywords = new Set(['as', 'typeof', 'keyof', 'satisfies', 'in', 'is']);

export function extractUnescapedQuotes(code: string): number[] {
  let changed = true;
  let offset = 0;
  let inJSX = false;
  let inJSXExpression = false;

  const changes: number[] = [];

  // keep processing until no more changes are needed
  while (changed) {
    changed = false;

    let currentToken: CurrentToken | null = null;

    const tokens = [...lexer(code.substring(offset))];

    // process each token in the code string
    for (let i = 0; i < tokens.length; i++) {
      const token = tokens[i];

      // track JSX context
      if (token.type === 'Punctuator') {
        if (token.value === '<') {
          inJSX = true;

          /**
           * Reset JSX expression flag when entering a new JSX element
           * to handle cases like {condition && <Component prop="value" />}.
           */
          inJSXExpression = false;
        } else if (token.value === '{' && inJSX) {
          inJSXExpression = true;
        } else if (token.value === '}' && inJSXExpression) {
          inJSXExpression = false;
        }
      } else if (token.type === 'RegularExpressionLiteral' && token.value.startsWith('/')) {
        inJSX = false;
        inJSXExpression = false;
      }

      if (currentToken) {
        // check if we need to continue processing after a change
        if (changed) {
          const delimiterPosition = token.value.indexOf(currentToken.delimiter);

          if (delimiterPosition !== -1) {
            offset += delimiterPosition + 1;
            break;
          }

          continue;
        }

        if (token.type === 'WhiteSpace') {
          offset += token.value.length;
          continue;
        }

        // skip escaping if we're in JSX context but not in a JSX expression
        if (inJSX && !inJSXExpression) {
          currentToken = null;
          offset += token.value.length;
          continue;
        }

        // never escape if the next non-whitespace token is a keyword
        if (token.type === 'IdentifierName' && keywords.has(token.value)) {
          currentToken = null;
          offset += token.value.length;
          continue;
        }

        if (
          token.type === 'IdentifierName' ||
          token.type === 'Invalid' ||
          token.type === 'NumericLiteral' ||
          token.type === 'PrivateIdentifier'
        ) {
          changes.push(currentToken.offset + currentToken.token.value.length - 1);
          changed = true;
          offset += token.value.length;
          continue;
        }

        currentToken = null;
      }

      // handle string literals and template literals
      if (token.type === 'StringLiteral' || token.type === 'NoSubstitutionTemplate') {
        const delimiter = token.value.charAt(0);
        currentToken = { delimiter, token, offset };
        offset += token.value.length;
        continue;
      }

      offset += token.value.length;
    }
  }

  return changes;
}

export async function transformUnescapedQuotes(code: MagicString, analyzer: AnalyzerWorker) {
  const changes = await analyzer.extractUnescapedQuotes(code.str);
  changes.forEach((change) => code.insert(change, '\\'));
}
